// QuickBooks Sync — Wave D admin page.
//
// Mirrors `tricivo_system/operations/quickbooks_sync.php`: Sync All button,
// notes input, and a paginated sync-log table. The actual sync runs server-
// side asynchronously (POST returns 202 with a run uuid). Per-run rows
// show counts + status; the per-record error log is rendered as JSON for
// admin debugging.

import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { PageHeader } from '../../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { Table, type Column } from '../../../../components/ui/Table/Table';
import { Pagination } from '../../../../components/ui/Pagination/Pagination';
import { StatusBadge } from '../../../../components/ui/StatusBadge/StatusBadge';
import { Tabs } from '../../../../components/ui/Tabs/Tabs';
import { Textarea } from '../../../../components/form/Textarea';
import { useQbStatus, useQbSyncLog, useQbTriggerSync } from './hooks/useQuickBooks';
import { useQbActiveModule } from './hooks/useQbActiveModule';
import { QbGroupSync } from './QbGroupSync';
import { QB_MODULE_TABS, QB_MODULE_INACTIVE_NOTE } from './qbModules';
import type { BadgeVariant } from '../../../../components/ui/Badge/Badge';
import type { QbSyncRunDto, QbSyncRunStatus } from '../../../../types/api';
import styles from './QuickBooksSyncPage.module.css';

const STATUS_MAP: Record<QbSyncRunStatus, BadgeVariant> = {
  running: 'info',
  completed: 'success',
  partial: 'warning',
  failed: 'danger',
};

const STATUS_LABELS: Record<QbSyncRunStatus, string> = {
  running: 'Running',
  completed: 'Completed',
  partial: 'Partial',
  failed: 'Failed',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function QuickBooksSyncPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  const [module, setModule] = useQbActiveModule();
  const status = useQbStatus(module);
  const log = useQbSyncLog(module, { page, limit: 25 });
  const trigger = useQbTriggerSync(module);
  const [notes, setNotes] = useState('');
  const isInactiveModule = module !== 'collections';

  function setPage(n: number) {
    const url = new URLSearchParams(params);
    url.set('page', String(n));
    setParams(url, { replace: false });
  }

  function handleTrigger(e: FormEvent) {
    e.preventDefault();
    trigger.mutate(
      { notes: notes.trim() ? notes.trim() : undefined },
      { onSuccess: () => setNotes('') },
    );
  }

  const runs = log.data?.runs ?? [];
  const pagination = log.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const columns: Column<QbSyncRunDto>[] = [
    {
      key: 'sync_started_at',
      header: 'Started',
      render: (_v, row) => formatDate(row.sync_started_at),
    },
    {
      key: 'sync_completed_at',
      header: 'Completed',
      render: (_v, row) => formatDate(row.sync_completed_at),
    },
    { key: 'records_attempted', header: 'Attempted', align: 'right' },
    { key: 'records_succeeded', header: 'Succeeded', align: 'right' },
    { key: 'records_failed', header: 'Failed', align: 'right' },
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => (
        <StatusBadge
          status={row.status}
          statusMap={STATUS_MAP}
          formatLabel={(s) => STATUS_LABELS[s] ?? s}
        />
      ),
    },
    {
      key: 'triggered_by_name',
      header: 'By',
      render: (_v, row) => row.triggered_by_name ?? 'Auto',
    },
  ];

  const isConnected = status.data?.connected ?? false;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Sync to QuickBooks"
        subtitle="Sync paid transactions to QuickBooks as expenses"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/collections/integrations/quickbooks')}
          >
            Back
          </Button>
        }
      />

      <Tabs
        items={QB_MODULE_TABS}
        value={module}
        onChange={(id) => setModule(id as typeof module)}
        ariaLabel="QuickBooks module"
      />

      {isInactiveModule ? (
        <p className={styles.moduleNote} role="note">
          {QB_MODULE_INACTIVE_NOTE}
        </p>
      ) : null}

      <Card title="Run a sync">
        {!isConnected ? (
          <p className={styles.muted}>
            QuickBooks is not connected. Connect on the integration page first.
          </p>
        ) : (
          <form className={styles.runForm} onSubmit={handleTrigger}>
            <Textarea
              aria-label="Notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes for this run…"
            />
            <Button
              variant="primary"
              icon={RefreshCw}
              type="submit"
              loading={trigger.isPending}
            >
              Sync All
            </Button>
          </form>
        )}
      </Card>

      {!isInactiveModule && isConnected ? <QbGroupSync /> : null}

      <Card title={`Sync log (${total})`} flush>
        <Table
          columns={columns}
          data={runs}
          keyField={(row) => row.uuid}
          loading={log.isLoading}
          emptyState={
            <div className={styles.empty}>
              <h3>No sync runs yet</h3>
              <p>Run a sync to see results.</p>
            </div>
          }
          ariaLabel="QuickBooks sync log"
        />
        {totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination
              pagination={{ page, limit: pagination?.limit ?? 25, total, totalPages }}
              onPageChange={setPage}
            />
          </div>
        ) : null}
      </Card>
    </div>
  );
}
